import classNames from "classnames";
import React, { memo } from "react";
import { SlaAssignee, SlaTaskListItem } from "../types";
import { STATUS_BADGE } from "../slaStatusDisplay";

interface Props {
  task: SlaTaskListItem;
  onClick: (task: SlaTaskListItem) => void;
}

const formatAssignees = (assignees: SlaAssignee[]): string => {
  const names = assignees.map((a) => `${a.firstname} ${a.surname}`.trim());
  if (names.length <= 3) return names.join(", ");
  return `${names.slice(0, 3).join(", ")}, +${names.length - 3} more`;
};

const SlaTaskRow = ({ task, onClick }: Props) => {
  const badge = STATUS_BADGE[task.sla_status] ?? STATUS_BADGE["no_sla"];
  const daysLabel =
    task.days_remaining >= 0
      ? `Due in ${task.days_remaining} day${task.days_remaining !== 1 ? "s" : ""}`
      : `${Math.abs(task.days_remaining)} day${Math.abs(task.days_remaining) !== 1 ? "s" : ""} overdue`;

  return (
    <div
      className="flex items-center justify-between py-3 px-4 border-b border-black-10 hover:bg-black-5 cursor-pointer"
      onClick={() => onClick(task)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onClick(task)}
    >
      <div className="flex-1 min-w-0">
        <div className="text-14 font-semibold text-black-80 truncate">{task.name}</div>
        <div className="text-12 text-black-60">
          {task.project_name}
          {task.property_name ? ` · ${task.property_name}` : ""}
          {task.phase ? ` · ${task.phase}` : ""}
        </div>
        {task.assignees.length > 0 && (
          <div className="text-12 text-black-40 truncate">
            Assigned to {formatAssignees(task.assignees)}
          </div>
        )}
        {task.assignees.length === 0 && (
          <div className="text-12 text-black-40 truncate">Assigned to -</div>
        )}
      </div>

      <div className="flex items-center gap-3 ml-4 flex-shrink-0">
        <div className="text-12 text-black-60 text-right">
          <div>{task.due_at}</div>
          <div>{daysLabel}</div>
        </div>
        <span
          className={classNames(
            "inline-block px-2 py-0 text-11 font-semibold rounded-full whitespace-no-wrap",
            badge.className
          )}
        >
          {badge.label}
        </span>
      </div>
    </div>
  );
};

export default memo(SlaTaskRow);
